import type { CaptureResult } from "posthog-js/dist/module.slim";

type Properties = CaptureResult["properties"];

/**
 * PostHog copies the page URL onto every event and onto the person
 * ($current_url, $initial_current_url). These query params must never travel
 * with it: the SSO callback, magic-link and password-reset pages carry live
 * auth tokens in theirs, and the search page carries what the user typed.
 */
const REDACTED_PARAMS = [
  "accessToken",
  "refreshToken",
  "refreshTokenExpiry",
  "token",
  "search",
];

const URL_PROPERTY = /url|referrer/i;

const redactUrl = (value: string): string => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return value;
  }
  const found = REDACTED_PARAMS.filter((name) => url.searchParams.has(name));
  if (!found.length) return value;
  found.forEach((name) => url.searchParams.set(name, "redacted"));
  return url.toString();
};

const redactProperties = (properties: Properties): Properties =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (typeof value === "string" && URL_PROPERTY.test(key)) {
        return [key, redactUrl(value)];
      }
      if ((key === "$set" || key === "$set_once") && value) {
        return [key, redactProperties(value as Properties)];
      }
      return [key, value];
    })
  );

/** posthog-js `before_send`: scrubs, never drops. */
export const redactUrlParams = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) return null;
  return {
    ...event,
    properties: redactProperties(event.properties),
    ...(event.$set && { $set: redactProperties(event.$set) }),
    ...(event.$set_once && { $set_once: redactProperties(event.$set_once) }),
  };
};
